import { NavLink, Outlet } from 'react-router-dom'
import { LogOut } from 'lucide-react'
import { useLogout, useSession } from '@/features/auth/use-session'
import { ROLE_LABEL } from '@/features/auth/types'
import { visibleNav } from '@/app/nav'
import { cn } from '@/lib/utils'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

function Logo() {
  return (
    <div className="flex items-center gap-2.5 px-4 pb-3 pt-5 text-base font-extrabold tracking-tight">
      <svg width="22" height="22" viewBox="0 0 26 26" fill="none" aria-hidden>
        <path
          d="M13 2 L23 7.5 V18.5 L13 24 L3 18.5 V7.5 Z"
          stroke="var(--primary)"
          strokeWidth="1.8"
          strokeLinejoin="round"
        />
      </svg>
      CoinWorldCap
    </div>
  )
}

export function ConsoleLayout() {
  const { user } = useSession()
  const logout = useLogout()
  if (!user) return null

  const groups = visibleNav(user.role)
  const initial = user.displayName.charAt(0).toUpperCase()

  return (
    <div className="flex h-full">
      <aside className="flex h-full w-[236px] shrink-0 flex-col border-r border-border bg-card">
        <Logo />
        <div className="px-4 pb-2 text-[10.5px] font-bold uppercase tracking-wider text-faint">
          Ops Console
        </div>

        <nav className="flex-1 overflow-y-auto px-2.5 py-1.5">
          {groups.map((group) => (
            <div key={group.label}>
              <div className="px-2 pb-1.5 pt-3.5 text-[10.5px] font-bold uppercase tracking-wider text-faint">
                {group.label}
              </div>
              {group.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  className={({ isActive }) =>
                    cn(
                      'mb-px flex items-center gap-2.5 rounded-lg border border-transparent px-2.5 py-2 text-[13.5px] font-semibold text-muted-foreground',
                      isActive
                        ? 'border-[#dbe3ff] bg-primary-soft text-accent-foreground'
                        : 'hover:bg-secondary hover:text-foreground',
                    )
                  }
                >
                  <item.icon className="size-[17px] shrink-0 opacity-85" />
                  {item.label}
                  {item.tag && (
                    <span className="ml-auto rounded-full bg-secondary px-1.5 py-px text-[9.5px] font-bold text-faint">
                      {item.tag}
                    </span>
                  )}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex items-center gap-2.5 border-t border-border p-4 text-left hover:bg-secondary"
            >
              <div className="grid size-8 shrink-0 place-items-center rounded-full bg-primary-soft text-xs font-bold text-accent-foreground">
                {initial}
              </div>
              <div className="min-w-0">
                <div className="truncate text-[13px] font-bold">{user.displayName}</div>
                <div className="truncate text-[11px] text-faint">{ROLE_LABEL[user.role]}</div>
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" side="top">
            <DropdownMenuItem onClick={() => logout.mutate()} disabled={logout.isPending}>
              <LogOut className="size-4" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </aside>

      <main className="min-w-0 flex-1 overflow-y-auto p-6">
        <Outlet />
      </main>
    </div>
  )
}
